import { apiRequest } from "@/lib/api/client";

// Integração real com as regras de notificação do Traccar (menu "Alertas" do
// drawer de veículo). Confirmado contra a instância em produção (Traccar
// 6.14.5, ver auditoria): o vínculo device <-> notification é feito via
// POST/DELETE /api/permissions {deviceId, notificationId}. GET /api/permissions
// quebra nesse servidor (StorageException: relation "tc_notification_device"
// does not exist — bug de nomenclatura da imagem 6.14.5), então a leitura do
// estado por device usa só GET /api/notifications?deviceId=, que já reflete
// corretamente os vínculos.
export interface TraccarNotification {
  id: number;
  type: string;
  description: string | null;
  always: boolean;
  calendarId: number;
  commandId: number;
  notificators: string;
  attributes: Record<string, unknown>;
}

export interface TraccarNotificationType {
  type: string;
}

function get<T>(path: string): Promise<T> {
  return apiRequest<T>(path, { method: "GET", credentials: "include" });
}

async function listTypes(): Promise<TraccarNotificationType[]> {
  return get<TraccarNotificationType[]>("/api/notifications/types");
}

async function listAll(): Promise<TraccarNotification[]> {
  return get<TraccarNotification[]>("/api/notifications");
}

async function listByDevice(deviceId: number): Promise<TraccarNotification[]> {
  return get<TraccarNotification[]>(`/api/notifications?deviceId=${deviceId}`);
}

async function create(type: string): Promise<TraccarNotification> {
  return apiRequest<TraccarNotification>("/api/notifications", {
    method: "POST",
    credentials: "include",
    body: { type, always: false, notificators: "web" },
  });
}

async function linkToDevice(deviceId: number, notificationId: number): Promise<void> {
  await apiRequest<void>("/api/permissions", {
    method: "POST",
    credentials: "include",
    body: { deviceId, notificationId },
  });
}

async function unlinkFromDevice(deviceId: number, notificationId: number): Promise<void> {
  await apiRequest<void>("/api/permissions", {
    method: "DELETE",
    credentials: "include",
    body: { deviceId, notificationId },
  });
}

export const notificationsService = {
  listTypes,
  listByDevice,

  async enableForDevice(deviceId: number, type: string): Promise<void> {
    // Reaproveita uma Notification já existente do mesmo tipo (pode já estar
    // vinculada a outro veículo) em vez de criar uma duplicata.
    const existing = (await listAll()).find((n) => n.type === type);
    const notification = existing ?? (await create(type));
    await linkToDevice(deviceId, notification.id);
  },

  async disableForDevice(deviceId: number, type: string): Promise<void> {
    const linked = await listByDevice(deviceId);
    const notification = linked.find((n) => n.type === type);
    if (!notification) return;
    await unlinkFromDevice(deviceId, notification.id);
  },
};
